// Globals
import { FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// Store
import { useAppDispatch, useAppState } from '@/store';

// Modules
import { Header } from '@/modules/Layout';
import { selectChannel } from '@/modules/Channels';
import { updateGoogleMerchantSettings } from '../../feature';

//Components
import { useForm } from '@/components';
import { CheckIcon } from '@/icons';
import { Button, Spinner } from '@/ui';
import { GoogleMerchantSettingsForm } from '../../components';
import { StyledContainer } from './MerchantSettingsContainerStyles';

// Helpers
import {
  MERCHANT_SETTINGS_FORM_ID,
  TGoogleMerchantSettingsFormValues,
  getMerchantSettingsInitialValues,
} from '../../helpers';
import { useGetGoogleMerchantSettings } from '../../hooks';

const MerchantSettingsContainer: FC = () => {
  const { t } = useTranslation(['common']);
  const dispatch = useAppDispatch();
  const currentChannel = useAppState(selectChannel);

  const { merchantSettings, isLoading } = useGetGoogleMerchantSettings(currentChannel?.id);
  const [formInstance] = useForm();

  const initialValues = useMemo(() => getMerchantSettingsInitialValues(merchantSettings), [merchantSettings]);

  useEffect(() => {
    if (merchantSettings) {
      formInstance.setFieldsValue(getMerchantSettingsInitialValues(merchantSettings));
    }
  }, [formInstance, merchantSettings]);

  const handleSubmitForm = async (values: TGoogleMerchantSettingsFormValues) => {
    if (!currentChannel) return;

    try {
      await dispatch(updateGoogleMerchantSettings({ ...values, channelId: currentChannel.id })).unwrap();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <>
      <Header title={t('menu_merchant_settings')} suptitle={t('menu_seo')}>
        <Button
          type="primary"
          size="small"
          loading={isLoading}
          form={MERCHANT_SETTINGS_FORM_ID}
          htmlType="submit"
          icon={<CheckIcon />}
        >
          {t('btn_save')}
        </Button>
      </Header>
      <Spinner spinning={isLoading}>
        <StyledContainer>
          <GoogleMerchantSettingsForm form={formInstance} initialValues={initialValues} onSubmit={handleSubmitForm} />
        </StyledContainer>
      </Spinner>
    </>
  );
};

export default MerchantSettingsContainer;
